import queries from 'api/queries'
import { message } from 'antd'
import { useLocalization } from 'localization'
import { useMutation, useQueryClient } from 'react-query'
import {
	acceptInvite,
	leaveOrganization,
	rejectInvite,
	removeClient,
	removeCPA,
	removeInvite,
	sendInvite,
} from './services'
import { IUserInvite } from 'api/users/models'
import { InviteStatus } from 'api/users/dtos'
import { ResponseErrorType } from 'types/custom'
import http, { CPA_ORG_ID_KEY } from 'api/http'

export const useRemoveCPA = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(removeCPA, {
		onSuccess: () => {
			message.success(t('auth.removeCPA.success'))
			queryClient.invalidateQueries([queries.cpas])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('auth.removeCPA.error'))
			}
		},
	})
}

export const useRemoveClient = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(removeClient, {
		onSuccess: () => {
			message.success(t('auth.removeClient.success'))
			queryClient.invalidateQueries([queries.clients])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('auth.removeClient.error'))
			}
		},
	})
}

export const useSendInvite = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(sendInvite, {
		onMutate: ({ email, type }) => {
			// update cache for invites
			queryClient.setQueryData<IUserInvite[]>([queries.senderInvites, type], invites =>
				invites!.map(invite => ({
					...invite!,
					status: InviteStatus.PENDING,
					resentRecently: invite.email === email || invite.sender.email === email ? true : invite.resentRecently,
				}))
			)
		},
		onSuccess: (_, { email, shouldRefetch = true }) => {
			message.success(t('auth.invite.success', { email }))
			if (shouldRefetch) {
				queryClient.invalidateQueries([queries.receiverInvites])
				queryClient.invalidateQueries([queries.senderInvites])
			}
		},
		onError: (error: ResponseErrorType, { email, type }) => {
			queryClient.setQueryData<IUserInvite[]>([queries.senderInvites, type], invites =>
				invites!.map(invite => ({
					...invite!,
					resentRecently: invite.email === email ? false : invite.resentRecently,
				}))
			)
			if (error?.response?.status !== 401) {
				message.error(t('auth.invite.error'))
			}
		},
	})
}

export const useAcceptInvite = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(acceptInvite, {
		onSuccess: () => {
			message.success(t('auth.acceptInvite.success'))
			queryClient.invalidateQueries([queries.receiverInvites])
			queryClient.invalidateQueries([queries.senderInvites])
			queryClient.invalidateQueries([queries.clients])
			queryClient.invalidateQueries([queries.current])
			queryClient.invalidateQueries([queries.cpas])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('auth.acceptInvite.error'))
			}
		},
	})
}

export const useRejectInvite = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(rejectInvite, {
		onSuccess: () => {
			message.success(t('auth.rejectInvite.success'))
			queryClient.invalidateQueries([queries.receiverInvites])
			queryClient.invalidateQueries([queries.senderInvites])
			queryClient.invalidateQueries([queries.cpas])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('auth.rejectInvite.error'))
			}
		},
	})
}

export const useRemoveInvite = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(removeInvite, {
		onSuccess: () => {
			message.success(t('auth.removeInvite.success'))
			queryClient.invalidateQueries([queries.senderInvites])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('auth.removeInvite.error'))
			}
		},
	})
}

export const useLeaveOrganization = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(leaveOrganization, {
		onSuccess: () => {
			message.success(t('organizations.leave.success'))
			queryClient.invalidateQueries([queries.current])
			queryClient.invalidateQueries([queries.receiverInvites])
			http.forgetStorageItem(CPA_ORG_ID_KEY)
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('organizations.leave.error'))
			}
		},
	})
}
